import { Link, type LinkProps } from '@tanstack/react-router';
import cn from 'classnames';
import { forwardRef } from 'react';

import { IconExternalLink } from 'components/UI/Icons/ExternalLink';
import { IconLoader } from 'components/UI/Icons/Loader';

type TAnchor = JSX.IntrinsicElements['a'];
type TButton = JSX.IntrinsicElements['button'];
type TLink = TAnchor & LinkProps;

export type Button = (TButton | Omit<TLink, 'onClick' | 'type'>) & {
  onClick?: TButton['onClick'];
  as?: React.ElementType;
  badge?: string | number | React.ReactNode;
  disableStyles?: boolean;
  iconEnd?: React.ReactNode;
  iconStart?: React.ReactNode;
  isLoading?: boolean;
  size?: 16 | 20 | 24 | 26 | 32 | 40 | 48;
  variant:
    | 'default-primary'
    | 'default-secondary'
    | 'default-tertiary'
    | 'default-quaternary'
    | 'default-quaternary_DEPRECATED'
    | 'default-link'
    | 'destructive-link'
    | 'destructive-primary'
    | 'destructive-secondary'
    | 'destructive-tertiary'
    | 'destructive-quaternary'
    | 'gray-secondary'
    | 'gray-tertiary'
    | 'gray-quaternary'
    | 'secondary_DEPRECATED'
    | 'secondary-alt_DEPRECATED';
};

export const Button = forwardRef<HTMLButtonElement, Button>(
  (
    {
      as = 'button',
      badge,
      children,
      className,
      disableStyles,
      disabled: _disabled,
      iconEnd: _iconEnd,
      iconStart,
      isLoading,
      size = 32,
      variant,
      ...props
    },
    ref,
  ) => {
    const isNewTabLink =
      'to' in props ? props.to && props.target === '_blank' : false;
    const Tag = 'to' in props ? Link : as;
    const disabled = _disabled || isLoading;

    let iconEnd = _iconEnd;
    if (_iconEnd === undefined && isNewTabLink) {
      iconEnd = <IconExternalLink />;
    }

    const isDeprecatedQuaternaryVariant = variant.endsWith(
      'quaternary_DEPRECATED',
    );

    const isDeprecatedVariant =
      variant.endsWith('DEPRECATED') && !isDeprecatedQuaternaryVariant;

    // Link variants don't have padding.
    const isLinkVariant = variant.endsWith('link');

    let isIconEnd = Boolean(iconEnd);
    let isIconStart = isLoading || Boolean(iconStart);
    const isBadge = badge !== undefined;
    // only string/number badges are supported in non-deprecated variants
    const isBadgeComponent = !(
      typeof badge === 'string' || typeof badge === 'number'
    );
    const isText = Boolean(children);
    const isIconOnly =
      (isIconStart || isIconEnd) &&
      !(isIconStart && isIconEnd) &&
      !isText &&
      !isBadge;
    const isBadgeEnd = isBadge && !isIconEnd;
    const isTextStart = isText && !isIconStart;
    const isTextEnd = isText && !iconEnd && !isBadgeEnd;

    if (isIconOnly) {
      // set other icon states to false for mutually exclusive class selectors
      isIconEnd = false;
      isIconStart = false;
    }

    return (
      <Tag
        className={cn(
          'group inline-flex flex-shrink-0 items-center border border-solid shadow-none outline-none transition duration-200 focus-visible:outline-offset-0',
          {
            '[&>svg:first-of-type]:animate-spin': isLoading,
            '[&>svg>path]:stroke-2 [&>svg]:h-4 [&>svg]:w-4':
              (size === 26 || size === 16) &&
              !isDeprecatedVariant &&
              isIconOnly,
            '[&>svg>path]:stroke-2 [&>svg]:h-6 [&>svg]:w-6':
              (size === 24 || size === 40) &&
              !isDeprecatedVariant &&
              isIconOnly,
            '[&>svg>path]:stroke-2 [&>svg]:h-[18px] [&>svg]:w-[18px]':
              size === 32 && !isDeprecatedVariant && isIconOnly,
            '[&>svg>path]:stroke-2 [&>svg]:h-[28px] [&>svg]:w-[28px]':
              size === 48 && !isDeprecatedVariant && isIconOnly,
            '[&>svg>path]:stroke-3 [&>svg]:h-3 [&>svg]:w-3':
              (size === 26 || size === 32) &&
              !isDeprecatedVariant &&
              !isIconOnly,
            '[&>svg>path]:stroke-3 [&>svg]:h-[18px] [&>svg]:w-[18px]':
              (size === 40 || size === 48) &&
              !isDeprecatedVariant &&
              !isIconOnly,
            'border-gray-300 bg-gray-300 text-gray-600':
              disabled && isDeprecatedVariant,
            'cursor-not-allowed opacity-40': disabled,
            'cursor-pointer': !disabled,
            'font-medium': isDeprecatedVariant,
            'h-10 gap-x-1.5 text-16/100% font-semibold':
              size === 40 && !isDeprecatedVariant,
            'h-12 gap-x-1.5 text-18/100% font-semibold':
              size === 48 && !isDeprecatedVariant,
            'h-6 gap-x-1.5 text-14/100% font-semibold':
              size === 24 && !isDeprecatedVariant,
            'h-8 gap-x-1.5 text-14/100% font-semibold':
              size === 32 && !isDeprecatedVariant,
            'h-[26px] gap-x-1.5 text-12/100% font-semibold':
              size === 26 && !isDeprecatedVariant,
            'justify-center': !isDeprecatedVariant,
            'px-1': size === 26 && !isDeprecatedVariant && isIconOnly,
            'px-1.5': size === 32 && !isDeprecatedVariant && isIconOnly,
            'px-3':
              size === 26 &&
              !isDeprecatedVariant &&
              !isDeprecatedQuaternaryVariant &&
              !isLinkVariant &&
              !isIconOnly,
            'px-4':
              (size === 32 || size === 40) &&
              !isDeprecatedVariant &&
              !isDeprecatedQuaternaryVariant &&
              !isLinkVariant &&
              !isIconOnly,
            'px-5':
              size === 48 &&
              !isDeprecatedVariant &&
              !isLinkVariant &&
              !isDeprecatedQuaternaryVariant,
            'px-[7px]':
              size === 40 &&
              !isDeprecatedVariant &&
              !isLinkVariant &&
              isIconOnly,
            'px-[9px]':
              size === 48 &&
              !isDeprecatedVariant &&
              !isLinkVariant &&
              isIconOnly,
            'rounded-[4px]': !isDeprecatedVariant && isIconOnly,
            'rounded-[8px]': !isDeprecatedVariant && !isIconOnly,
          },
          !disableStyles && {
            'border-blue-500 bg-transparent text-blue-500 focus-visible:border-blue-500 focus-visible:bg-blue-50 focus-visible:!outline-1 focus-visible:outline-blue-500':
              variant === 'default-tertiary',
            'border-gray-400 bg-transparent text-gray-600 focus-visible:border-gray-500 focus-visible:bg-gray-50 focus-visible:!outline-1 focus-visible:outline-gray-500':
              variant === 'gray-tertiary',
            'border-red-700 bg-transparent text-red-700 focus-visible:border-red-600 focus-visible:!outline-1 focus-visible:outline-red-600':
              variant === 'destructive-tertiary',
            'border-transparent bg-blue-500 text-gray-200 focus-visible:border-blue-1100 focus-visible:!outline-1 focus-visible:outline-blue-1100':
              variant === 'default-primary',
            'border-transparent bg-blue-500/8 text-blue-500 focus-visible:border-blue-500 focus-visible:bg-blue-500/16 focus-visible:!outline-1 focus-visible:outline-blue-500':
              variant === 'default-secondary',
            'border-transparent bg-gray-100 text-gray-600 focus-visible:border-gray-600 focus-visible:bg-gray-200 focus-visible:!outline-1 focus-visible:outline-gray-600':
              variant === 'gray-secondary',
            'border-transparent bg-red-700 text-gray-200 focus-visible:border-red-600 focus-visible:!outline-1 focus-visible:outline-red-600':
              variant === 'destructive-primary',
            'border-transparent bg-red-700/20 text-red-700 focus-visible:border-red-600 focus-visible:bg-red-700/20 focus-visible:!outline-1 focus-visible:outline-red-600':
              variant === 'destructive-secondary',
            'border-transparent bg-transparent text-blue-500 focus-visible:!outline-1 focus-visible:outline-blue-500':
              variant === 'default-quaternary_DEPRECATED' && isIconOnly,
            'border-transparent bg-transparent text-blue-500 focus-visible:underline':
              variant === 'default-quaternary_DEPRECATED' && !isIconOnly,
            'border-transparent bg-transparent text-gray-600 hover:text-gray-600 focus-visible:border-gray-600 focus-visible:bg-transparent focus-visible:!outline-1 focus-visible:outline-gray-600':
              variant === 'gray-quaternary',
            'border-transparent bg-transparent text-red-700 focus-visible:!outline-1 focus-visible:outline-red-700':
              variant === 'destructive-quaternary' && isIconOnly,
            'border-transparent bg-transparent text-red-700 focus-visible:underline':
              variant === 'destructive-quaternary' && !isIconOnly,
            'border-transparent text-blue-500 hover:text-blue-500 hover:underline focus:text-blue-500 focus:underline active:text-blue-500 active:underline':
              variant === 'default-link',
            'border-transparent text-red-700 hover:text-red-700 hover:underline focus:text-red-700 focus:underline active:text-red-700 active:underline':
              variant === 'destructive-link',
            'focus-visible:border-gray-800 focus-visible:bg-white focus-visible:text-gray-800 focus-visible:outline-focus-secondary-button active:border-gray-800 active:bg-white active:text-gray-800':
              variant === 'secondary_DEPRECATED',
            'focus-visible:border-white focus-visible:bg-gray-700 focus-visible:text-white focus-visible:outline-1 focus-visible:outline-gray-700 active:border-white active:bg-gray-700 active:text-white':
              variant === 'secondary-alt_DEPRECATED',
            'focus-visible:outline-1': variant === 'secondary-alt_DEPRECATED',
            'focus-visible:outline-2': variant === 'secondary_DEPRECATED',
          },
          !disabled &&
            !disableStyles && {
              // TODO: hover shadows for destructive-primary
              'active:border-red-600': variant === 'destructive-primary',

              'border-gray-400 bg-white text-gray-800 hover:border-gray-800 hover:bg-gray-200 hover:text-gray-800':
                variant === 'secondary_DEPRECATED',

              'border-gray-700 bg-gray-700 text-gray-200 hover:border-gray-600 hover:bg-gray-600 hover:text-white':
                variant === 'secondary-alt_DEPRECATED',

              'border-transparent bg-transparent text-blue-500 hover:text-blue-500 focus:text-blue-500 active:text-blue-500':
                variant === 'default-quaternary',

              'focus-visible:!outline-1 focus-visible:outline-blue-500':
                variant === 'default-quaternary' && isIconOnly,

              'hover:bg-blue-50 focus:border-blue-500 focus:bg-blue-100 active:border-blue-500 active:bg-blue-100':
                variant === 'default-quaternary' && !isIconOnly,

              'hover:bg-blue-50 hover:text-blue-500 active:border-blue-500 active:bg-blue-100 data-[open]:bg-blue-50':
                variant === 'default-tertiary',

              'hover:bg-blue-500/12 hover:text-blue-500 active:border-blue-1100 active:bg-blue-500/16':
                variant === 'default-secondary',

              'hover:bg-blue-500/8 hover:text-blue-500 active:bg-blue-500/16 active:text-blue-500':
                variant === 'default-quaternary_DEPRECATED' && isIconOnly,

              'hover:bg-gray-100 active:border-gray-200 active:bg-gray-200 data-[open]:bg-gray-100':
                variant === 'gray-quaternary',

              'hover:bg-gray-200 active:border-gray-600 active:bg-gray-200 data-[open]:bg-gray-200':
                variant === 'gray-secondary',

              'hover:bg-gray-50 hover:text-gray-600 active:border-gray-500 active:bg-gray-100 data-[open]:bg-gray-50':
                variant === 'gray-tertiary',

              'hover:bg-red-700/24 active:border-red-600 active:bg-red-700/28':
                variant === 'destructive-secondary',

              'hover:bg-red-700/8 active:border-red-700 active:bg-red-700/12':
                variant === 'destructive-tertiary',

              'hover:bg-red-700/8 hover:text-red-700 active:bg-red-700/12 active:text-red-700':
                variant === 'destructive-quaternary' && isIconOnly,

              'hover:text-blue-500 hover:underline active:underline':
                variant === 'default-quaternary_DEPRECATED' && !isIconOnly,
              // TODO: hover shadows for default-primary
              'hover:text-gray-200 active:border-blue-1100 active:text-gray-200':
                variant === 'default-primary',
              'hover:text-red-700 hover:underline active:underline':
                variant === 'destructive-quaternary' && !isIconOnly,
            },
          size === 20 &&
            isDeprecatedVariant && {
              'h-[20px] gap-x-1 rounded-[10px] text-12/100% [&>svg]:h-3 [&>svg]:w-3': true,
              'pe-1': isIconEnd,
              'pe-2': isTextEnd,
              'pe-[3px]': isBadgeEnd,
              'ps-1': isIconStart,
              'ps-2': isTextStart,
              'px-[3px]': isIconOnly,
            },
          size === 26 &&
            isDeprecatedVariant && {
              'h-[26px] gap-x-1 rounded-[13px] text-14/100% [&>svg]:h-4 [&>svg]:w-4': true,
              'pe-0.5': isBadgeEnd,
              'pe-1.5': isIconEnd,
              'pe-3': isTextEnd,
              'ps-1.5': isIconStart,
              'ps-3': isTextStart,
              'px-1': isIconOnly,
            },
          size === 32 &&
            isDeprecatedVariant && {
              'h-8 gap-x-1.5 rounded-[16px] text-14/100% [&>svg]:h-[18px] [&>svg]:w-[18px]': true,
              'pe-1': isBadgeEnd,
              'pe-2': isIconEnd,
              'pe-3': isTextEnd && isIconStart,
              'pe-4': isTextEnd && !isIconStart,
              'ps-2': isIconStart,
              'ps-4': isTextStart,
              'px-1.5': isIconOnly,
            },
          size === 40 &&
            isDeprecatedVariant && {
              'h-10 gap-x-1.5 rounded-[20px] text-16/100% [&>svg]:h-[18px] [&>svg]:w-[18px]': true,
              'pe-2.5': isIconEnd || isBadgeEnd,
              'pe-4': isTextEnd,
              'ps-2.5': isIconStart,
              'ps-4': isTextStart,
              'px-2.5': isIconOnly,
            },
          className,
        )}
        {...(isNewTabLink
          ? {
              rel: 'noopener noreferrer',
            }
          : {})}
        {...(as !== 'div'
          ? {
              disabled,
            }
          : {})}
        {...(as === 'button'
          ? {
              type: 'button',
            }
          : {})}
        ref={ref}
        {...props}
      >
        {isLoading ? <IconLoader /> : iconStart}
        {typeof children === 'function'
          ? children({
              isActive: false,
              isTransitioning: false,
            })
          : children}
        {isBadge && !isBadgeComponent && (
          <span
            className={cn(
              'flex items-center justify-center rounded-[14px] font-semibold transition duration-200',
              {
                'bg-blue-500 text-white':
                  variant === 'default-secondary' ||
                  variant === 'default-tertiary' ||
                  variant === 'default-quaternary',
                'bg-gray-300 text-gray-700 group-focus:bg-gray-800 group-focus:text-white group-active:bg-gray-800 group-active:text-white':
                  !disabled && variant === 'secondary_DEPRECATED',
                'bg-gray-400 text-gray-600':
                  disabled && variant === 'secondary_DEPRECATED',
                'bg-gray-500 text-gray-600':
                  disabled && variant === 'secondary-alt_DEPRECATED',
                'bg-gray-600 text-gray-300 group-hover:bg-gray-500 group-hover:text-gray-100 group-focus:bg-white group-focus:text-gray-800 group-active:bg-white group-active:text-gray-800':
                  !disabled && variant === 'secondary-alt_DEPRECATED',
                'bg-red-700 text-white':
                  variant === 'destructive-secondary' ||
                  variant === 'destructive-tertiary',
                'bg-white text-blue-500': variant === 'default-primary',
                'bg-white text-gray-900': variant === 'destructive-primary',
                'h-3.5 min-w-3.5 px-1.5 text-10/100%':
                  size === 26 && !isDeprecatedVariant,
                'h-5 min-w-5 px-1.5 text-12/100%':
                  size === 32 && !isDeprecatedVariant,
                'h-[22px] min-w-[22px] px-2 text-12/100%':
                  (size === 40 || size === 48) && !isDeprecatedVariant,
                'ms-1 h-5 px-1.5 text-12/100%':
                  size === 26 && isDeprecatedVariant,
                'ms-1 h-[22px] px-2 text-12/100%':
                  (size === 32 || size === 40) && isDeprecatedVariant,
              },
            )}
          >
            {badge}
          </span>
        )}
        {isBadge && isBadgeComponent && (
          <span
            className={cn('flex items-center justify-center', {
              // TODO: add badge styles for size === 20
              'ms-1 h-5': size === 26,
              'ms-1 h-[22px]': size === 32 || size === 40,
            })}
          >
            {badge}
          </span>
        )}
        {iconEnd}
      </Tag>
    );
  },
);
